import { useEffect, useMemo, useState } from "react";
import { getSpecsMap } from "../../../utils/productSpecs";
import { calculateMatchScore } from "../../../utils/calculateMatchScore";
import { capMatchPercent } from "../../../utils/matchScoreUtils";
import { useUserAuth } from "../../../contexts/UserAuthContext";
import { fetchProductMatchExplanation } from "../../../utils/fetchProductMatchExplanation";
import {
  buildAiSessionMatchPayload,
} from "../../../utils/resolveProductMatch";
import {
  hasMatchIntent,
  resolveMatchIntent,
  formatBudgetRangeLabel,
} from "../../../utils/resolveMatchIntent";
import { getAiSessionDependencyKey } from "../../../utils/homeChatMatchPersistence";

const buildClientBudgetReason = (score, budget, productPrice) => {
  if (!budget) return "";
  const label = formatBudgetRangeLabel(budget);
  const min = budget.min != null ? Number(budget.min) : null;
  const max = budget.max != null ? Number(budget.max) : null;
  const price = productPrice != null ? Number(productPrice) : null;
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max);

  if (hasMax && price != null && price > max) {
    if (score < 60) {
      const over = Math.round(price - max);
      // Budget is always stored/entered in USD today, so this stays USD
      // regardless of the visitor's region — only the locale is pinned so the
      // number format doesn't vary with the browser's locale.
      return `$${over.toLocaleString("en-US")} over your $${max.toLocaleString("en-US")} budget`;
    }
    if (label) return `Slightly above your ${label} budget`;
    return "Budget fit could be better";
  }
  if (hasMin && price != null && price < min) {
    return label ? `Below your ${label} budget` : "Below your budget";
  }
  if (label) return `Fits your ${label} budget`;
  return score >= 80 ? "Within your budget" : "Budget fit could be better";
};

const useProductInsights = ({ product, categoryFields, aiSession = null }) => {
  const { user } = useUserAuth();
  const sessionKey = useMemo(
    () => getAiSessionDependencyKey(aiSession),
    [aiSession],
  );
  const [wizardData, setWizardData] = useState(null);
  const [enhancedMatchData, setEnhancedMatchData] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [identityRequired, setIdentityRequired] = useState(false);
  const [identityCompleteness, setIdentityCompleteness] = useState(null);
  const [lowConfidence, setLowConfidence] = useState(false);

  const specs = useMemo(() => {
    if (!product) return [];

    if (Array.isArray(product.specifications) && product.specifications.length) {
      const categoryIconMap = new Map(
        (Array.isArray(categoryFields) ? categoryFields : []).map((f) => [
          String(f?.name || "").toLowerCase(),
          {
            icon: f?.icon || "",
            iconClass: f?.iconClass || "",
            type: f?.type || "",
          },
        ])
      );

      const specsFromProduct = product.specifications
        .filter(
          (s) => s?.value !== undefined && s?.value !== null && s?.value !== ""
        )
        .map((s) => {
          const key = (s.key || s.label || "").toLowerCase();
          const categoryIcon = categoryIconMap.get(key);
          let icon = s.icon || categoryIcon?.icon || "";
          if (!icon) {
            if (key === "brand") icon = "FaIndustry";
            else if (key === "model") icon = "FaSteamSquare";
            else if (key === "year") icon = "FaCalendar";
          }
          return {
            key: s.key || s.label,
            label: s.label || s.key,
            value: s.value,
            icon,
            iconClass: s.iconClass || categoryIcon?.iconClass || "",
            type: s.type || categoryIcon?.type || "",
          };
        });

      const base = [
        {
          key: "brand",
          label: "Brand",
          value: product?.brand?.name || product?.brand,
          icon: "FaIndustry",
        },
        {
          key: "model",
          label: "Model",
          value: product?.model,
          icon: "FaSteamSquare",
        },
        {
          key: "year",
          label: "Year",
          value: product?.year,
          icon: "FaCalendar",
        },
      ].filter((x) => x.value !== undefined && x.value !== null && x.value !== "");

      const merged = [...base, ...specsFromProduct];
      const seen = new Set();
      const allSpecs = merged.filter((s) => {
        const id = (s.key || s.label || "").toLowerCase();
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      const priorityKeys = ["brand", "model", "year"];
      return [
        ...allSpecs.filter((s) =>
          priorityKeys.includes((s.key || "").toLowerCase())
        ),
        ...allSpecs.filter(
          (s) => !priorityKeys.includes((s.key || "").toLowerCase())
        ),
      ];
    }

    const specsMap = getSpecsMap(product);

    const fromCategory =
      (Array.isArray(categoryFields) ? categoryFields : [])
        .map((f) => {
          const v =
            specsMap?.[f.name] ??
            specsMap?.[f.name?.toLowerCase?.()] ??
            product?.[f.name] ??
            product?.[f.name?.toLowerCase?.()] ??
            undefined;
          if (v === undefined || v === null || v === "") return null;

          return {
            key: f.name,
            label: f.label || f.name,
            value: v,
            icon: f.icon || "",
            iconClass: f.iconClass || "",
            type: f.type || "",
          };
        })
        .filter(Boolean) || [];

    const base = [
      {
        key: "brand",
        label: "Brand",
        value: product?.brand?.name || product?.brand,
        icon: "FaIndustry",
      },
      {
        key: "model",
        label: "Model",
        value: product?.model,
        icon: "FaSteamSquare",
      },
      {
        key: "year",
        label: "Year",
        value: product?.year,
        icon: "FaCalendar",
      },
    ].filter((x) => x.value);

    const merged = [...fromCategory, ...base];
    const seen = new Set();
    const allSpecs = merged.filter((s) => {
      const id = (s.key || s.label || "").toLowerCase();
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const priorityKeys = ["brand", "model", "year"];
    return [
      ...allSpecs.filter((s) =>
        priorityKeys.includes((s.key || "").toLowerCase())
      ),
      ...allSpecs.filter(
        (s) => !priorityKeys.includes((s.key || "").toLowerCase())
      ),
    ];
  }, [product, categoryFields]);

  const aiSessionMatch = useMemo(
    () => buildAiSessionMatchPayload(product, aiSession),
    [product, sessionKey],
  );

  const resolvedEnhancedMatchData = useMemo(() => {
    if (!aiSessionMatch) return enhancedMatchData;
    return {
      overallScore: aiSessionMatch.overallScore,
      dimensionsList: aiSessionMatch.dimensionsList,
      intentSnapshot: aiSessionMatch.intentSnapshot,
      dealBreakerFailed: aiSessionMatch.dealBreakerFailed,
    };
  }, [aiSessionMatch, enhancedMatchData]);

  const calculateMatchPercentage = useMemo(() => {
    if (!product) return null;
    if (identityRequired) return null;

    if (aiSessionMatch?.matchInsights) {
      return aiSessionMatch.matchInsights;
    }

    // Logged-in: wait for server match only (no localStorage client calc).
    if (user && matchLoading && !resolvedEnhancedMatchData) return null;
    if (user && !resolvedEnhancedMatchData) return null;

    if (!user && !wizardData) return null;

    if (resolvedEnhancedMatchData) {
      const { overallScore, dimensionsList, intentSnapshot } =
        resolvedEnhancedMatchData;
      if (overallScore == null) return null;

      if (dimensionsList && dimensionsList.length > 0) {
        const budget = intentSnapshot?.budget || wizardData?.budget;
        const dimensionsWithReasons = dimensionsList.map((dim) => {
          let reason = dim.reason || "";
          if (dim.key === "budget" && budget && !reason) {
            reason = buildClientBudgetReason(
              dim.score,
              budget,
              product?.price,
            );
          }
          return {
            name: dim.name,
            score: dim.score,
            reason,
            icon: dim.icon || "FaCheckCircle",
            key: dim.key,
          };
        });

        return {
          percentage: capMatchPercent(overallScore),
          dimensions: dimensionsWithReasons,
          reasons: dimensionsWithReasons
            .filter((dim) => dim.score >= 70)
            .slice(0, 3)
            .map((dim) => dim.reason),
          intentSnapshot: intentSnapshot || null,
          dealBreakerFailed: resolvedEnhancedMatchData.dealBreakerFailed || false,
        };
      }

      const { dimensions } = resolvedEnhancedMatchData;
      const dimensionNames = {
        feature: "Feature Match",
        value: "Value Score",
        budget: "Budget Fit",
        review: "Review Quality",
        specCoverage: "Spec Coverage",
        availability: "Spec Coverage",
        trend: "Popularity",
      };

      const dimensionsWithReasons = Object.entries(dimensions)
        .filter(([key, score]) => score != null && key !== "availability")
        .map(([key, score]) => {
          let reason = "";
          let icon = "FaCheckCircle";

          if (key === "budget") {
            const budget =
              intentSnapshot?.budget || wizardData?.budget;
            if (budget) {
              reason = buildClientBudgetReason(
                score,
                budget,
                product?.price,
              );
            }
            icon = "FaDollarSign";
          } else if (key === "feature") {
            reason =
              score >= 80
                ? "Strong feature alignment with your priorities"
                : "Good feature match";
          } else if (key === "value") {
            reason =
              score >= 80
                ? "Excellent value for the price"
                : "Good value proposition";
            icon = "FaStar";
          } else if (key === "review") {
            reason = score >= 80 ? "Highly rated by users" : "Good user reviews";
            icon = "FaStar";
          } else if (key === "specCoverage") {
            reason =
              score >= 80
                ? "Most requested features found in specs"
                : "Some requested features found in product specs";
          } else {
            reason = `${dimensionNames[key] || key}: ${Math.round(score)} fit score`;
          }

          return {
            name: dimensionNames[key] || key,
            score: Math.round(Math.min(100, Math.max(0, score))),
            reason,
            icon,
            key,
          };
        });

      return {
        percentage: capMatchPercent(overallScore),
        dimensions: dimensionsWithReasons,
        reasons: dimensionsWithReasons
          .filter((dim) => dim.score >= 70)
          .slice(0, 3)
          .map((dim) => dim.reason),
        intentSnapshot:
          intentSnapshot || resolvedEnhancedMatchData.intentProfile || null,
        dealBreakerFailed: resolvedEnhancedMatchData.dealBreakerFailed || false,
      };
    }

    const matchResult = calculateMatchScore(product, wizardData, specs);
    if (!matchResult) return null;

    const reasons = [];
    const dimensionsWithReasons = matchResult.dimensions.map((dim) => {
      let reason = "";
      let icon = "FaCheckCircle";

      if (dim.name.includes("Budget")) {
        const budget = wizardData.budget;
        if (budget) {
          reason = buildClientBudgetReason(
            dim.score,
            budget,
            product?.price,
          );
        }
        icon = "FaDollarSign";
      } else if (dim.name === "Brand") {
        reason =
          dim.score === 100
            ? `Matches your preferred brand: ${wizardData.brand}`
            : "Different brand, but similar quality";
        icon = "FaIndustry";
      } else if (
        dim.name.toLowerCase().includes("warranty") ||
        dim.name.toLowerCase().includes("support")
      ) {
        reason =
          dim.score >= 90
            ? "Includes warranty & support - essential for organizations"
            : "Warranty information not specified";
      } else if (dim.name.toLowerCase().includes("reliability")) {
        reason =
          dim.score >= 90
            ? "High reliability rating - suitable for business use"
            : "Reliability information available";
      } else if (dim.name.toLowerCase().includes("security")) {
        reason =
          dim.score >= 90
            ? "Security features included - important for organizations"
            : "Security features not explicitly listed";
      } else {
        const matchingSpec = specs.find((s) => {
          const specLabel = (s.label || s.key || "").toLowerCase();
          return specLabel.includes(dim.name.toLowerCase());
        });

        if (matchingSpec && matchingSpec.value) {
          reason = `${dim.name}: ${matchingSpec.value}`;
        } else if (dim.score >= 70) {
          reason = `Includes ${dim.name} - matches your needs`;
        } else {
          reason = `${dim.name} not explicitly listed`;
        }

        const iconMap = {
          battery: "FaBatteryFull",
          "battery life": "FaBatteryFull",
          camera: "FaCamera",
          performance: "FaTachometerAlt",
          "screen size": "FaMobileAlt",
          resolution: "FaDesktop",
          "smart tv": "FaTv",
          hdr: "FaSun",
          weight: "FaWeight",
          gpu: "FaMicrochip",
          touchscreen: "FaHandPointer",
          os: "FaOperatingSystem",
        };
        icon = iconMap[dim.name.toLowerCase()] || "FaCheckCircle";
      }

      if (dim.score >= 70) reasons.push(reason);
      return { ...dim, reason, icon };
    });

    return {
      percentage: matchResult.percentage,
      dimensions: dimensionsWithReasons,
      reasons: reasons.slice(0, 3),
    };
  }, [
    wizardData,
    product,
    specs,
    resolvedEnhancedMatchData,
    identityRequired,
    user,
    matchLoading,
    aiSessionMatch,
  ]);

  useEffect(() => {
    const resolved = resolveMatchIntent({ aiSession, user });
    const next =
      resolved.wizardAnswers && hasMatchIntent(resolved.wizardAnswers)
        ? resolved.wizardAnswers
        : user
          ? resolved.wizardAnswers || {}
          : resolved.wizardAnswers;

    setWizardData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [user, sessionKey]);

  useEffect(() => {
    if (!product?._id) return;
    if (aiSessionMatch) {
      setEnhancedMatchData(null);
      setIdentityRequired(false);
      setIdentityCompleteness(null);
      setLowConfidence(false);
      setMatchLoading(false);
      return;
    }
    if (!user && !wizardData) {
      setIdentityRequired(false);
      setIdentityCompleteness(null);
      setLowConfidence(false);
      return;
    }

    let cancelled = false;
    setMatchLoading(true);

    const load = async () => {
      const userId = user?.id || user?._id || null;
      const result = await fetchProductMatchExplanation(product._id, {
        user,
        userId,
        aiSession,
      });
      if (cancelled) return;
      const requiresIdentity = result?.identityRequired === true;
      setIdentityRequired(requiresIdentity);
      setIdentityCompleteness(result?.scoring?.identityCompleteness || null);
      setLowConfidence(Boolean(result?.lowConfidence));
      if (result?.match && !requiresIdentity) {
        setEnhancedMatchData(result.match);
      } else {
        setEnhancedMatchData(null);
      }
      setMatchLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [product?._id, user, wizardData, aiSessionMatch, sessionKey]);

  const hasResolvedMatchIntent = hasMatchIntent(wizardData);

  // Canonical budget for the whole page: whatever budget actually backed the
  // hero match score (server intentSnapshot, or the AI-session snapshot when
  // that's the active source) always wins over the locally-resolved
  // `wizardData`. Without this, sections that read `wizardData.budget`
  // directly (spec highlights, budget analysis, over-budget checks) can
  // disagree with the hero's own "fits your $X budget" reasoning, since the
  // hero score's intent may come from a different resolution path (e.g. the
  // server's Shopper Model or an active AI session) than `wizardData`.
  const canonicalBudget =
    calculateMatchPercentage?.intentSnapshot?.budget ||
    wizardData?.budget ||
    aiSessionMatch?.intentSnapshot?.budget ||
    null;

  const effectiveWizardData = useMemo(() => {
    if (!canonicalBudget) return wizardData;
    if (wizardData && JSON.stringify(wizardData.budget) === JSON.stringify(canonicalBudget)) {
      return wizardData;
    }
    return { ...(wizardData || {}), budget: canonicalBudget };
  }, [wizardData, canonicalBudget]);

  return {
    wizardData: effectiveWizardData,
    hasResolvedMatchIntent,
    enhancedMatchData: resolvedEnhancedMatchData,
    specs,
    calculateMatchPercentage,
    identityRequired,
    identityCompleteness,
    lowConfidence,
    matchLoading: aiSessionMatch ? false : matchLoading,
  };
};

export default useProductInsights;
